<template>
  <div class="task-list-view">
    <!--
      Single flat table for BOTH modes — "My Tasks" (`showAssigneeColumn`
      false: already just the current user's own tasks) and "My Team's
      Tasks" (`showAssigneeColumn` true: every direct report's tasks
      together, with an "Employee Name" column — see `tableFields` — as
      the leftmost column instead of the old per-employee section
      headers, so there's exactly one sortable/scrollable table to scan
      regardless of scope). Below 768px, `stacked="md"` renders each row as
      a card-style block instead of a wide horizontal table.
    -->
    <b-table
      striped
      hover
      responsive
      stacked="md"
      :items="tableRows"
      :fields="tableFields"
      :tbody-tr-class="rowClassFor"
      show-empty
      :busy="loading"
    >
      <template #table-busy>
        <div class="text-center text-muted py-2">Loading...</div>
      </template>

      <template #cell(title)="data">
        <div class="d-flex align-items-center">
          <div class="text-nowrap font-weight-bold">
            {{ data.item.title }}
            <feather-icon
              v-if="isRecurringRow(data.item)"
              icon="RepeatIcon"
              size="12"
              class="text-muted ml-25"
              :aria-label="data.item.isRecurring ? 'Recurring task — repeats automatically' : 'Auto-generated from a recurring task'"
            />
          </div>
          <!--
            "Who else is on this batch" — only rendered for a
            multi-assignee task (non-null `groupId`); see
            `CoAssigneeAvatars.vue`'s own doc and `coAssigneesFor()` in
            `task-config.js`. Lives next to the title here (rather than
            the due-date cell, per `TaskCard.vue`) since this row's "Due
            Date" cell is already fairly busy with escalation/"Due Today"
            badges.
          -->
          <co-assignee-avatars :co-assignees="coAssigneesFor(data.item, allTasks)" class="ml-50" />
          <!-- In-app ping badge (email-free) — same treatment as `TaskCard.vue`. -->
          <b-badge
            v-if="isPinged(data.item)"
            pill
            variant="light-warning"
            class="ml-50 text-nowrap task-ping-badge"
          >
            <feather-icon icon="BellIcon" size="10" class="mr-25" />{{ pingBadgeLabelFor(data.item) }}
          </b-badge>
          <template v-if="attachmentFor(data.item)">
            <a
              v-if="attachmentFor(data.item).url"
              :href="attachmentFor(data.item).url"
              target="_blank"
              rel="noopener"
              class="ml-50 text-nowrap small d-inline-flex align-items-center"
              style="max-width: 160px;"
              @click.stop
            >
              <feather-icon icon="PaperclipIcon" size="12" class="mr-25 flex-shrink-0" />
              <span class="text-truncate">{{ attachmentFor(data.item).name }}</span>
            </a>
            <span
              v-else
              class="ml-50 text-nowrap small text-muted d-inline-flex align-items-center"
              style="max-width: 160px;"
            >
              <feather-icon icon="PaperclipIcon" size="12" class="mr-25 flex-shrink-0" />
              <span class="text-truncate">{{ attachmentFor(data.item).name }}</span>
            </span>
          </template>
        </div>
        <div v-if="data.item.description" class="text-muted small task-desc-cell">{{ data.item.description }}</div>
      </template>

      <!--
        Team mode ("Employee Name" column): the name + ping bell only
        render on the FIRST row of each employee's contiguous block
        (`isFirstOfEmployeeGroup` — `tableRows` is already grouped/ordered
        by employee, see its doc) — every OTHER row in that same block
        renders this cell BLANK instead of repeating the name down the
        whole table (the "duplicacy" fix). `rowClassFor` below draws a
        thin separator above each block's first row so the blank cells in
        between still read as visually grouped with the name above them,
        not as orphaned/broken rows. My mode (`!showAssigneeColumn`) skips
        all of this — a single person's tasks have nothing to group, and
        there's no one to ping on your own row.
      -->
      <template #cell(assignee)="data">
        <template v-if="showAssigneeColumn">
          <div v-if="isFirstOfEmployeeGroup(data.item)" class="d-flex align-items-center task-employee-cell">
            <span class="text-nowrap">{{ assigneeNameFor(data.item) }}</span>
            <b-badge
              v-if="hasEmployeePing(data.item)"
              pill
              variant="light-warning"
              class="ml-25 text-nowrap task-ping-badge"
            >
              <feather-icon icon="BellIcon" size="10" class="mr-25" />Nudge
            </b-badge>
            <b-button
              v-if="canPingFor(data.item)"
              size="sm"
              :variant="hasEmployeePing(data.item) ? 'flat-warning' : 'flat-secondary'"
              class="p-25 ml-25"
              aria-label="Ping about all their tasks (in-app nudge)"
              @click="$emit('ping', pingTargetFor(data.item))"
            >
              <feather-icon icon="BellIcon" size="13" />
            </b-button>
          </div>
        </template>
        <template v-else>
          {{ assigneeNameFor(data.item) }}
        </template>
      </template>

      <template #cell(status)="data">
        <b-badge :variant="statusBadgeVariant(data.item)">{{ normalizeTaskStatus(data.item) }}</b-badge>
      </template>

      <template #cell(priority)="data">
        <!-- `boundary="window"` alone only affects Popper's flip/placement math, not where the menu DOM node lives — it's still clipped by any ancestor `overflow` (here, `b-table`'s `responsive` wrapper). `positionFixed: true` (via `popper-opts`) renders the menu with `position: fixed`, which escapes ancestor overflow clipping entirely — see the matching comment on this same dropdown in `TaskCard.vue`. -->
        <b-dropdown
          v-if="canChangePriorityFor(data.item)"
          size="sm"
          variant="flat-secondary"
          toggle-class="p-0"
          no-caret
          boundary="window"
          :popper-opts="{ positionFixed: true }"
        >
          <template #button-content>
            <b-badge :variant="priorityBadgeVariant(data.item)" pill>{{ normalizeTaskPriority(data.item) }}</b-badge>
          </template>
          <b-dropdown-item
            v-for="level in priorityLevels"
            :key="level.key"
            @click="$emit('change-priority', { task: data.item, priority: level.key })"
          >
            {{ level.label }}
          </b-dropdown-item>
        </b-dropdown>
        <b-badge v-else :variant="priorityBadgeVariant(data.item)" pill>{{ normalizeTaskPriority(data.item) }}</b-badge>
      </template>

      <template #cell(dueDate)="data">
        <span :class="{ 'text-danger font-weight-bold': isOverdue(data.item), 'text-info font-weight-bold': isDueTodayRow(data.item) }">
          {{ (data.item.dueDate || data.item.due_date) ? dueDateDisplay(data.item) : '-' }}
        </span>
        <b-badge v-if="isDueTodayRow(data.item)" variant="info" pill class="ml-50 text-nowrap">Due Today</b-badge>
        <b-badge
          v-if="escalationLevelFor(data.item)"
          pill
          :variant="escalationMetaFor(data.item).variant"
          class="ml-50 text-nowrap"
        >
          {{ escalationMetaFor(data.item).label }}
        </b-badge>
        <b-badge
          v-if="showOverdueBadge(data.item)"
          :variant="overdueMeta.variant"
          pill
          class="ml-50 text-nowrap"
        >
          {{ overdueMeta.label }}
        </b-badge>
      </template>

      <template #cell(actions)="data">
        <div class="d-flex task-actions-group" style="gap: 4px;">
          <b-button
            size="sm"
            variant="outline-secondary"
            aria-label="View"
            @click="$emit('view', data.item)"
          >
            <feather-icon icon="EyeIcon" size="14" />
          </b-button>
          <b-button
            v-if="canEditFor(data.item)"
            size="sm"
            variant="outline-primary"
            aria-label="Edit"
            @click="$emit('edit', data.item)"
          >
            <feather-icon icon="EditIcon" size="14" />
          </b-button>
          <b-button
            v-if="canDeleteFor(data.item)"
            size="sm"
            variant="outline-danger"
            aria-label="Delete"
            @click="$emit('delete', data.item)"
          >
            <feather-icon icon="Trash2Icon" size="14" />
          </b-button>
          <b-button
            v-if="!canEditFor(data.item) && !canDeleteFor(data.item) && !canChangeStatusFor(data.item) && !canPingFor(data.item)"
            size="sm"
            variant="outline-secondary"
            aria-label="Only the creator can update this task"
            disabled
          >
            <feather-icon icon="LockIcon" size="14" />
          </b-button>
          <!--
            Per-TASK ping — distinct from the "Employee Name" cell's
            per-employee name-bell above (`pingTargetFor` — "ping about
            everything"), this one pings about ONLY this row's task
            (`pingTaskTargetFor` — see its doc). Same `canPingFor()` gate
            as the name-bell reused as-is, since both are governed by the
            exact same manager/direct-report relationship — just applied
            per-row here instead of once per employee block.
          -->
          <b-button
            v-if="canPingFor(data.item)"
            size="sm"
            :variant="isPinged(data.item) ? 'outline-warning' : 'outline-secondary'"
            aria-label="Ping about this task (in-app nudge)"
            @click="$emit('ping', pingTaskTargetFor(data.item))"
          >
            <feather-icon icon="BellIcon" size="14" />
          </b-button>
        </div>
      </template>

      <template #empty>
        <div class="text-center text-muted py-2">No tasks found.</div>
      </template>
    </b-table>
  </div>
</template>

<script>
import { BTable, BBadge, BButton, BDropdown, BDropdownItem } from 'bootstrap-vue'
import moment from 'moment'
import CoAssigneeAvatars from './CoAssigneeAvatars.vue'
import {
  normalizeTaskStatus,
  normalizeTaskPriority,
  statusVariant,
  priorityVariant,
  taskId,
  taskAssigneeEmpId,
  taskAssigneeName,
  TASK_PRIORITY_LEVELS,
  TASK_STATUS_COLUMNS,
  isDueToday,
  getEscalationLevel,
  escalationLevelMeta,
  isPartOfRecurringSeries,
  formatDueDateTime,
  isTaskOverdue,
  TASK_OVERDUE_META,
  coAssigneesFor,
  normalizeTaskAttachment,
} from '@/utils/task-config'
import { sortTasksForColumn } from '@/utils/task-order-store'
import {
  canEditTaskDetails,
  canChangeStatus,
  canChangePriority,
  canDeleteTask,
  canPingTask,
} from '@/utils/task-permissions'
import { hasAllowedStatusTransition } from '@/utils/project-permissions'
import { projectId as getProjectId } from '@/utils/project-config'
import { getTaskPing, getEmployeePing } from '@/utils/task-ping-store'

export default {
  name: 'TaskListView',
  components: { BTable, BBadge, BButton, BDropdown, BDropdownItem, CoAssigneeAvatars },
  props: {
    tasks: { type: Array, default: () => [] },
    /** Full unscoped task list — used ONLY for `groupId`-sibling lookups (`coAssigneesFor()`, see the `title` cell template above), unrelated to `tasks`'s own (already-scoped/filtered) rows. */
    allTasks: { type: Array, default: () => [] },
    currentUser: { type: Object, default: () => ({}) },
    teamMembers: { type: Array, default: () => [] },
    loading: { type: Boolean, default: false },
    /**
     * "My Team's Tasks" mode — adds an "Employee Name" column (leftmost
     * — see `tableFields`) naming whose task each row is, and flattens
     * every direct report's tasks into `tableRows` in `teamMembers` order
     * instead of the old one-`<b-table>`-per-employee section layout.
     * Prop name kept as-is (`showAssigneeColumn`) since `TaskBoard.vue`
     * already passes `scope === 'team'` through it.
     */
    showAssigneeColumn: { type: Boolean, default: true },
    /** Bumped after each Ping so list badges re-read `task-ping-store`. */
    pingRevision: { type: Number, default: 0 },
    /** All visible projects — for project-scoped status transition checks. */
    projects: { type: Array, default: () => [] },
  },
  data() {
    return {
      priorityLevels: TASK_PRIORITY_LEVELS,
      overdueMeta: TASK_OVERDUE_META,
    }
  },
  computed: {
    /**
     * Team mode ("My Team's Tasks") gets an explicit "Employee Name"
     * column as the LEFTMOST field — per spec: "add an Employee Name
     * column — no separate headline" — since there's no more per-employee
     * section header naming the assignee once for a whole block of rows;
     * every row now needs to say whose task it is on its own. Flat ("My
     * Tasks") mode keeps its own pre-existing "Assignee" column
     * unchanged (always just the current user, but named explicitly —
     * see the git history for why) — this prop only ever toggles which
     * ONE of the two labels/positions applies, never both at once.
     */
    tableFields() {
      const fields = []
      if (this.showAssigneeColumn) fields.push({ key: 'assignee', label: 'Employee Name' })
      fields.push({ key: 'title', label: 'Task' })
      if (!this.showAssigneeColumn) fields.push({ key: 'assignee', label: 'Assignee' })
      fields.push(
        { key: 'status', label: 'Status' },
        { key: 'priority', label: 'Priority' },
        { key: 'dueDate', label: 'Due Date' },
        { key: 'actions', label: 'Actions' },
      )
      return fields
    },
    /**
     * Team mode: every direct report's tasks concatenated into ONE flat
     * array, in `teamMembers` order, each member's own tasks internally
     * sorted via `sortTasksForColumn` (same priority-then-due-date sort
     * the old per-employee sections used) — `null` outside team mode, so
     * `tableRows` below falls back to `tasks` as-is for "My Tasks". A
     * member with zero tasks simply contributes nothing (no empty
     * placeholder row) — the "Employee Name" column on THEIR neighbors'
     * rows already conveys who's who, so an empty section had no
     * remaining purpose once headers went away.
     */
    groupedTaskRows() {
      if (!this.showAssigneeColumn) return null
      const byEmpId = {}
      this.tasks.forEach(task => {
        const empId = taskAssigneeEmpId(task)
        if (empId == null) return
        if (!byEmpId[empId]) byEmpId[empId] = []
        byEmpId[empId].push(task)
      })
      const rows = []
      this.teamMembers.forEach(member => {
        const key = this.memberEmpId(member)
        rows.push(...sortTasksForColumn(byEmpId[key] || []))
      })
      return rows
    },
    /** What the single `<b-table>` actually renders — `groupedTaskRows` (Team mode) or plain `tasks` (My mode, already just the current user's own tasks, no re-sorting needed). */
    tableRows() {
      return this.groupedTaskRows || this.tasks
    },
    /**
     * Ids of the tasks that are the FIRST row of their employee's
     * contiguous block within `tableRows` — Team mode only (`null`
     * outside it). Backs `isFirstOfEmployeeGroup()`/`rowClassFor()` below,
     * which together collapse a repeated "Employee Name" cell down to
     * just once per employee (the "duplicacy" fix) instead of printing
     * the same name on every one of their task rows. Computed as a `Set`
     * (not re-scanned per row via e.g. `tableRows.indexOf`) so rendering
     * stays a single O(n) pass over the table regardless of how many rows
     * it has.
     */
    firstOfEmployeeGroupIds() {
      if (!this.showAssigneeColumn) return null
      const ids = new Set()
      let lastEmpId = null
      this.tableRows.forEach(task => {
        const empId = taskAssigneeEmpId(task)
        if (empId !== lastEmpId) {
          ids.add(taskId(task))
          lastEmpId = empId
        }
      })
      return ids
    },
  },
  methods: {
    normalizeTaskStatus,
    normalizeTaskPriority,
    statusVariant,
    priorityVariant,
    coAssigneesFor,
    attachmentFor(task) {
      return normalizeTaskAttachment(task)
    },
    /** Soft `light-*` counterpart to `priorityVariant()` — see `TaskCard.vue#priorityBadgeVariant`'s doc comment for why (matches that same card-face treatment here so Board <-> List switching never changes a badge's color style, only its layout). */
    priorityBadgeVariant(task) {
      return `light-${priorityVariant(task)}`
    },
    /** Soft `light-*` counterpart to `statusVariant()` — see `priorityBadgeVariant()` just above. */
    statusBadgeVariant(task) {
      return `light-${statusVariant(task)}`
    },
    memberEmpId(member) {
      return String(member.emp_id ?? member.id)
    },
    canEditFor(task) {
      return canEditTaskDetails(this.currentUser, task)
    },
    canDeleteFor(task) {
      return canDeleteTask(this.currentUser, task)
    },
    canChangePriorityFor(task) {
      return canChangePriority(this.currentUser, task, this.teamMembers)
    },
    canChangeStatusFor(task) {
      if (!canChangeStatus(this.currentUser, task)) return false
      const project = this.projectForTask(task)
      if (!project) return true
      return hasAllowedStatusTransition(this.currentUser, project, task, TASK_STATUS_COLUMNS)
    },
    projectForTask(task) {
      const pid = task?.projectId ?? task?.project_id
      if (pid == null) return null
      return this.projects.find(p => String(getProjectId(p)) === String(pid)) || null
    },
    /** See `firstOfEmployeeGroupIds`'s doc — `true` outside Team mode (a single person's tasks have nothing to group). */
    isFirstOfEmployeeGroup(task) {
      if (!this.firstOfEmployeeGroupIds) return true
      return this.firstOfEmployeeGroupIds.has(taskId(task))
    },
    /**
     * `<b-table>`'s `tbody-tr-class` — draws a thin separator ABOVE each
     * employee's first row (`.task-list-row-group-start`, see the
     * `<style>` below), so the blank "no name repeated" rows in between
     * (see `isFirstOfEmployeeGroup`) still read as visually grouped with
     * the named row above them rather than looking like stray/broken
     * rows. Skips the table's very first row (nothing to separate it
     * FROM) and My mode entirely (nothing to group there at all).
     */
    rowClassFor(task) {
      if (!this.showAssigneeColumn || !task) return ''
      const rows = this.tableRows
      if (rows[0] === task) return ''
      return this.isFirstOfEmployeeGroup(task) ? 'task-list-row-group-start' : ''
    },
    /**
     * Ping gate — `canPingTask` (manager of the assignee), deliberately
     * NOT `canEditTaskDetails` / `createdBy`. A manager may ask for an
     * update on any of a direct report's tasks, including ones they did
     * not personally create or assign. Independent of which UI tab is
     * open; Team-mode templates already hide the control outside that
     * view via `isFirstOfEmployeeGroup` / `showAssigneeColumn`.
     */
    canPingFor(task) {
      return canPingTask(this.currentUser, task, this.teamMembers)
    },
    pingMetaFor(task) {
      // Depend on `pingRevision` so localStorage-only pings re-render in the table.
      // eslint-disable-next-line no-unused-expressions
      this.pingRevision
      return getTaskPing(task) || getEmployeePing(taskAssigneeEmpId(task))
    },
    isPinged(task) {
      return !!this.pingMetaFor(task)
    },
    hasEmployeePing(task) {
      // eslint-disable-next-line no-unused-expressions
      this.pingRevision
      return !!getEmployeePing(taskAssigneeEmpId(task))
    },
    pingBadgeLabelFor(task) {
      const count = Number(this.pingMetaFor(task) && this.pingMetaFor(task).pingCount) || 0
      return count > 1 ? `Pinged ×${count}` : 'Pinged'
    },
    /**
     * `{ empId, empName }` payload for the per-EMPLOYEE name-bell's `ping`
     * event ("ping about everything they have") — deliberately OMITS
     * `task` (even though `task` is the argument used to resolve
     * empId/empName off this employee's block) so `TaskBoard.vue
     * #handlePingEmployee` falls back to its fully-generic behavior. See
     * `pingTaskTargetFor()` just below for the per-row/per-task
     * counterpart, which DOES include `task` — that field's presence vs.
     * absence is the one thing distinguishing the two payloads/paths.
     */
    pingTargetFor(task) {
      return {
        empId: taskAssigneeEmpId(task),
        empName: this.assigneeNameFor(task),
      }
    },
    /** `{ empId, empName, task }` payload for the per-ROW ping button's `ping` event ("ping about THIS task") — see `pingTargetFor()`'s doc just above for why the per-employee name-bell's payload deliberately omits `task` while this one includes it. */
    pingTaskTargetFor(task) {
      return {
        empId: taskAssigneeEmpId(task),
        empName: this.assigneeNameFor(task),
        task,
      }
    },
    /** Delegates to `isTaskOverdue()` in `task-config.js` so this red-text cutoff always agrees with `escalationLevelFor()`'s badge — including the same-day 6 PM IST default deadline when no `dueTime` is set. */
    isOverdue(task) {
      return isTaskOverdue(task)
    },
    isDueTodayRow(task) {
      return isDueToday(task)
    },
    escalationLevelFor(task) {
      return getEscalationLevel(task)
    },
    escalationMetaFor(task) {
      return escalationLevelMeta(this.escalationLevelFor(task)) || { label: '', variant: 'danger' }
    },
    /** Past deadline with no escalation tier (e.g. recurring occurrences). */
    showOverdueBadge(task) {
      return this.isOverdue(task) && !this.isDueTodayRow(task) && !this.escalationLevelFor(task)
    },
    formatDate(value) {
      return moment(value).format('DD MMM YYYY')
    },
    /** "22 Jul 2026, 5:00 PM" when the task has a `dueTime` set, otherwise just the date — see `formatDueDateTime()` in `task-config.js`. */
    dueDateDisplay(task) {
      return formatDueDateTime(task, this.formatDate)
    },
    /** Backs the shared `#cell(assignee)` template — "Employee Name" in Team mode, "Assignee" in My mode (see `tableFields`). */
    assigneeNameFor(task) {
      return taskAssigneeName(task)
    },
    isRecurringRow(task) {
      return isPartOfRecurringSeries(task)
    },
  },
}
</script>

<style lang="scss" scoped>
.task-desc-cell {
  max-width: 320px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.task-employee-cell {
  gap: 4px;
}

// Thin separator ABOVE every employee's first row in Team mode (see
// `rowClassFor()`'s doc) — the visual cue that a new employee's block of
// tasks has started, now that there's no per-employee section header
// doing that job. `::v-deep` because `tbody-tr-class` puts this straight
// on the `<b-table>`-rendered `<tr>` itself, a child BootstrapVue creates
// outside this component's own scoped-style boundary.
::v-deep tr.task-list-row-group-start > td {
  border-top: 2px solid rgba(0, 0, 0, 0.08);
}

// Mobile / tablet: `stacked="md"` (see template) makes BootstrapVue
// re-render every row as a label/value block below 768px instead of a
// horizontally-scrolled table — the one part that treatment doesn't touch
// is sizing WITHIN a cell, so the icon-only View/Edit/Delete/Locked
// buttons in the `actions` cell stay at their desktop `size="sm"`
// footprint (comfortably clickable with a mouse, too small a tap target
// on a touchscreen). Widened here, scoped to narrow viewports only, so
// desktop's compact action row is untouched.
@media (max-width: 767px) {
  ::v-deep .task-actions-group .btn {
    min-width: 44px;
    min-height: 44px;
    padding: 0.5rem 0.65rem;
  }
}
</style>
